import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { DateHelper } from 'src/app/shared/helpers/date-helper';
import { batchNoFormatValidator } from 'src/app/stocks/shared/validators';
import { EasyNotificationService } from 'src/app/services/easy-notification.service';
import { NewBatchDialogRow } from './models/new-batch-dialog';

@Component({
  selector: 'app-stocks-new-batch-dialog',
  templateUrl: './new-batch-dialog.component.html',
  styleUrls: ['./new-batch-dialog.component.css']
})
export class NewBatchDialogComponent implements OnChanges {
  /** 是否顯示 */
  @Input() show = false;
  /** 待建新批號列 (父層偵測後預帶; 元件開窗時補有效日期預設值) */
  @Input() rows: NewBatchDialogRow[] = [];

  /** 過期/製造日判斷的基準日 (單據日期 ChangeDate)；未提供/無效時 fallback 今天。 */
  @Input() referenceDate: Date | string = null;

  /** 是否允許建立「有效日期已過期」的新批號，預設 false 維持原行為 (入庫類單據擋已過期新批號) */
  @Input() allowExpired = false;

  /** 確認: 回傳補齊後的列 (批號已正規化大寫) */
  @Output() confirm = new EventEmitter<NewBatchDialogRow[]>();
  /** 取消: 回單據編輯模式 */
  @Output() cancel = new EventEmitter<void>();

  /** 防重複送出 */
  isSaving = false;
  /** 允許過期模式下，目前是否有「有效日期已過期」的列 (供中性提醒顯示；recompute 快取，模板 O(1) 讀取) */
  hasExpiredRows = false;
  /** grid 內重複 (子商品|批號) 集合, 供紅底 O(1) 查詢 */
  private duplicateKeys = new Set<string>();
  /** 每列錯誤旗標快取 (於 recompute 更新, 模板 O(1) 讀取, 避免每次變更偵測重跑 regex/Date) */
  private errorMap = new Map<NewBatchDialogRow, { batchNo: boolean; mfg: boolean; exp: boolean }>();

  constructor(private notification: EasyNotificationService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.show && changes.show.currentValue === true) {
      this.isSaving = false;
      // 不在開窗時用「今天」預推有效日期; 僅在使用者輸入製造日期時才自動帶 (見 onManufactureDateChange)
      this.recompute();
    }
  }

  // ── 列事件 ──
  onBatchNoChange(row: NewBatchDialogRow, value: string): void {
    row.BatchNo = (value || '').toUpperCase();
    this.recompute();
  }

  onManufactureDateChange(row: NewBatchDialogRow, value: Date): void {
    row.ManufactureDate = value || null;
    if (row.ManufactureDate && !row.ExpirationDate && row.expirationDays && row.expirationDays > 0) {
      row.ExpirationDate = DateHelper.addDay(new Date(row.ManufactureDate), row.expirationDays - 1);
    }
    this.recompute();
  }

  onExpirationDateChange(row: NewBatchDialogRow, value: Date): void {
    row.ExpirationDate = value || null;
    this.recompute();
  }

  // ── 紅底驗證 (讀 recompute 快取的旗標, 模板 O(1); 與 canConfirm 邏輯一致) ──
  hasBatchNoError(row: NewBatchDialogRow): boolean {
    return this.errorMap.get(row)?.batchNo ?? false;
  }

  hasManufactureDateError(row: NewBatchDialogRow): boolean {
    return this.errorMap.get(row)?.mfg ?? false;
  }

  hasExpirationDateError(row: NewBatchDialogRow): boolean {
    return this.errorMap.get(row)?.exp ?? false;
  }

  onConfirm(): void {
    if (this.isSaving) { return; }
    // 確定鈕不 disable; 點擊時驗證並以訊息明確指出哪筆批號哪裡錯 (對齊批號明細維護)
    const error = this.firstValidationError();
    if (error) {
      this.notification.showWarning(error);
      return;
    }
    this.isSaving = true;
    const result = (this.rows || []).map(r => ({ ...r, BatchNo: (r.BatchNo || '').trim().toUpperCase() }));
    this.confirm.emit(result);
  }

  /** 回傳第一筆驗證錯誤訊息 (無錯回 null); 訊息明確指出批號與原因 */
  private firstValidationError(): string | null {
    if (!this.rows || this.rows.length === 0) {
      return '沒有要新增的批號';
    }
    this.recompute();  // 確保重複集合最新
    for (const r of this.rows) {
      const batchNo = (r.BatchNo || '').trim().toUpperCase();
      if (!batchNo) {
        return '批號不可為空';
      }
      if (batchNoFormatValidator({ value: batchNo } as AbstractControl)) {
        return `批號「${batchNo}」僅接受英文、數字、底線(_)、橫線(-)、點(.)、斜線(/)`;
      }
      if (this.isAfterReference(r.ManufactureDate)) {
        return `批號「${batchNo}」的製造日期不可晚於單據日期`;
      }
      if (!this.allowExpired && r.ExpirationDate && this.isPast(r.ExpirationDate)) {
        return `批號「${batchNo}」的有效日期不可早於單據日期(已過期)`;
      }
      if (r.ManufactureDate && r.ExpirationDate && r.ExpirationDate < r.ManufactureDate) {
        return `批號「${batchNo}」的有效日期不可早於製造日期`;
      }
      if (this.duplicateKeys.has(this.rowKey(r))) {
        return `批號「${batchNo}」重複`;
      }
    }
    return null;
  }

  onCancel(): void {
    this.cancel.emit();
  }

  // ── helpers ──
  private rowKey(row: NewBatchDialogRow): string {
    return row.BatchProductId + '|' + (row.BatchNo || '').trim().toUpperCase();
  }

  /** 重算 grid 內重複批號集合 + 每列錯誤旗標 (供紅底 O(1) 讀取) */
  private recompute(): void {
    // 1. 重複 (子商品|批號) 集合
    const count = new Map<string, number>();
    for (const r of this.rows || []) {
      if (!(r.BatchNo || '').trim()) { continue; }
      const k = this.rowKey(r);
      count.set(k, (count.get(k) || 0) + 1);
    }
    const dup = new Set<string>();
    count.forEach((c, k) => { if (c >= 2) { dup.add(k); } });
    this.duplicateKeys = dup;

    // 2. 每列錯誤旗標 (一次算好, 模板免每次變更偵測重跑 regex/Date)
    const map = new Map<NewBatchDialogRow, { batchNo: boolean; mfg: boolean; exp: boolean }>();
    for (const r of this.rows || []) {
      map.set(r, {
        batchNo: this.computeBatchNoError(r),
        mfg: this.isAfterReference(r.ManufactureDate),
        exp: this.computeExpirationError(r)
      });
    }
    this.errorMap = map;

    // 3. 允許過期模式的中性提醒旗標 (不擋、不紅底, 只提示)
    this.hasExpiredRows = this.allowExpired
      && (this.rows || []).some(r => r.ExpirationDate && this.isPast(r.ExpirationDate));
  }

  /** 批號欄錯誤 (空白/格式/重複) */
  private computeBatchNoError(row: NewBatchDialogRow): boolean {
    const batchNo = (row.BatchNo || '').trim().toUpperCase();
    if (!batchNo) { return true; }
    if (batchNoFormatValidator({ value: batchNo } as AbstractControl)) { return true; }
    return this.duplicateKeys.has(this.rowKey(row));
  }

  /** 有效日期錯誤 (過期/早於製造日; 空白選填不算錯; allowExpired 時「已過期」不算錯) */
  private computeExpirationError(row: NewBatchDialogRow): boolean {
    if (!row.ExpirationDate) { return false; }
    if (!this.allowExpired && this.isPast(row.ExpirationDate)) { return true; }
    if (row.ManufactureDate && row.ExpirationDate < row.ManufactureDate) { return true; }
    return false;
  }

  /** 取得基準日 (midnight)：優先單據日期 referenceDate, 未提供/無效則今天 */
  private getReferenceDate(): Date {
    let base = new Date();
    if (this.referenceDate) {
      const parsed = new Date(this.referenceDate);
      if (!isNaN(parsed.getTime())) { base = parsed; }
    }
    return new Date(base.getFullYear(), base.getMonth(), base.getDate());
  }

  /** 有效日早於基準日 (已過期) */
  private isPast(date: Date | null): boolean {
    if (!date) { return false; }
    const d = new Date(date);
    const day = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    return day < this.getReferenceDate();
  }

  /** 製造日晚於基準日 (不可晚於單據日期) */
  private isAfterReference(date: Date | null): boolean {
    if (!date) { return false; }
    const d = new Date(date);
    const day = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    return day > this.getReferenceDate();
  }
}
